/**
 * The screen's two reads, the deck they seed, and everything a card needs to word itself.
 *
 * **The same cache the Pulpit uses.** `attentionQueueQueryOptions()` and `rosterQueryOptions()` are
 * `entities/client`'s, so a card answered here disappears from the Pulpit's bento and vice versa —
 * there is one queue, not two. Defining a key here would have given the trainer two lists that
 * drift apart and two reads against an endpoint limited to 30 a minute.
 *
 * Three states, never a branch on the data: `pending` while either read is open, `error` when the
 * queue failed, `empty` when it answered with nothing. `x.length === 0` cannot tell the first from
 * the third, and every jump and every copy defect of the September round came from that conflation.
 *
 * ── The deck is the SITTING's, and that is why it is state ───────────────────────────────────
 * A card the trainer answered stays in place, struck through, so the index never jumps and "3 z 14"
 * has a denominator. That is a fact about this visit rather than about the server, so a background
 * refetch is FOLDED into what is on screen (`reconcileDeck`) rather than replacing it.
 *
 * ── Back to `/zadania` is back to the same sitting ───────────────────────────────────────────
 * A card's clip opens the one player on the client's card, and closing it returns here through
 * the browser's Back (owner, 25 September 2026) — which REMOUNTS this screen. A fresh seed would
 * select the first open card and forget every answer struck through, so the deck the trainer
 * stepped away from is kept against the history entry it was worked on, and a return to THAT
 * entry resumes it: the same card selected, the answered ones in place, and the answer that
 * brought them back folded in by the same `reconcileDeck` a background read takes. Only the last
 * sitting is kept, in memory: a reload, or a fresh visit through the rail (a new entry), starts
 * clean, which is what "a sitting, not an address" has always meant here.
 */

import { useQueries } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { useSessionUser } from '~/entities/session';

import {
  attentionQueueQueryOptions,
  daysSinceLastWorkout,
  rosterQueryOptions,
  type AttentionReason,
} from '~/entities/client';
import {
  attentionQueue,
  mutedReasonsOf,
  useHomeFormats,
  type AttentionSectionDeps,
} from '~/features/attention-queue';

import { reconcileDeck, seedDeck, type DeckScope, type TaskDeck } from '../model/taskDeck';

export type TasksStatus = 'pending' | 'error' | 'empty' | 'ready';

export interface TasksScreenState {
  readonly status: TasksStatus;
  readonly deck: TaskDeck;
  readonly setDeck: React.Dispatch<React.SetStateAction<TaskDeck | null>>;
  /** The reasons this trainer has muted (R16, R18). Named here whether or not the queue is empty. */
  readonly mutedReasons: readonly AttentionReason[];
  /**
   * The sitting's clock, frozen on the visit's first render: the day every card's weeks and months
   * are drawn around, and the instant its "9 dni" is counted to — the same one the queue's
   * sentences were worded against.
   */
  readonly now: Date;
  readonly retry: () => void;
}

/** What the desk reads before the first answer lands. Never drawn — the screen is `pending` then. */
const EMPTY_DECK: TaskDeck = { cards: [], scope: 'all', selectedKey: null };

/** The last sitting, and the history entry it belongs to. See the header. */
const lastSitting: { current: { readonly entryKey: string; readonly deck: TaskDeck } | null } = {
  current: null,
};

/** `scope` is the half the address asked for; it seeds the deck once and is the trainer's after. */
export function useTasksScreen(scope: DeckScope): TasksScreenState {
  const { formatDate } = useHomeFormats();

  const [roster, queue] = useQueries({
    queries: [rosterQueryOptions(), attentionQueueQueryOptions()],
  });

  // Seeded ONCE, on the visit's first render. `new Date()` in the body is a fresh value every
  // render, which no memoization can see past. Nothing on this screen is a live clock.
  const [now] = useState(() => new Date());

  const lapsedDays = daysSinceLastWorkout(roster.data, now);
  const selfCustomerId = useSessionUser()?.selfCustomerId ?? null;
  /** How a row's sentence is worded. The pure projection takes both as parameters. */
  const wording: AttentionSectionDeps = {
    formatDate,
    daysSinceLastWorkout: (customerId) =>
      customerId === null ? undefined : lapsedDays.get(customerId),
    selfCustomerId,
  };

  /**
   * The same pair, held for the effect below.
   *
   * `formatDate` is a fresh closure on every render, so an effect that DEPENDED on it would fire on
   * every render, allocate a new deck and re-render — a loop that only the React Compiler's
   * memoization was standing between this screen and, and the first version of this file said so
   * wrongly. The effect depends on the two READS instead, which are TanStack's and change exactly
   * when an answer lands, and reaches the formatters through here. Written in an effect, never
   * during render.
   */
  const wordingRef = useRef(wording);
  useEffect(() => {
    wordingRef.current = wording;
  });

  // The entry this sitting was STARTED on, frozen: the router moves its location to the player's
  // entry while this screen is still mounted, and a live key would file the deck under that one.
  const [entryKey] = useState(
    useLocation({ select: (location) => location.state.__TSR_key ?? null }),
  );
  const [deck, setDeck] = useState<TaskDeck | null>(() => {
    const left = lastSitting.current;
    return left !== null && left.entryKey === entryKey ? left.deck : null;
  });
  useEffect(() => {
    if (entryKey !== null && deck !== null) lastSitting.current = { entryKey, deck };
  }, [entryKey, deck]);
  const answered = queue.isSuccess;

  /* The deck is SESSION state fed by an external store, which is the one shape an effect is for.
   *
   * It cannot be derived: a card the server has stopped returning stays in the deck, struck through
   * in its own place (R5), and that is history rather than a projection of the current answer. It
   * cannot be folded in an event handler either, because the event is "a background read landed",
   * which React gives no handler for.
   *
   * The dependencies are the two READS and nothing else. `setDeck` changes neither, so the effect
   * runs once per answer and settles — and that is a property of the dependency list rather than an
   * assumption about what the compiler memoized. */
  useEffect(() => {
    if (!answered) return;
    const entries = attentionQueue(queue.data, wordingRef.current).entries;
    // The muted list travels with the same answer, so a kind switched off on `/zadania/rodzaje`
    // leaves the deck with the read that confirms it rather than being struck through as "done".
    const muted = mutedReasonsOf(queue.data);
    setDeck((current) =>
      current === null ? seedDeck(entries, scope) : reconcileDeck(current, entries, muted),
    );
  }, [answered, queue.data, roster.data, scope]);

  // The muted reasons the foot names (R18). Through the selector rather than the whole projection:
  // this depends on `mutedReasons` alone, and the screen would otherwise reword every sentence in
  // the queue on every render to read a list of at most fifteen strings.
  const mutedReasons = mutedReasonsOf(queue.data);

  // A RETRY is a wait. An errored TanStack query keeps `status: 'error'` and `isPending: false`
  // while it refetches, so „Spróbuj ponownie" left the failure on screen until the answer landed
  // and then put the deck up in one frame, with no wait in between for the gate to hand over from.
  // `isFetching` over an ERRORED read is the retry and nothing else.
  const status: TasksStatus =
    queue.isPending ||
    roster.isPending ||
    (queue.isError && queue.isFetching) ||
    (roster.isError && roster.isFetching)
      ? 'pending'
      : queue.isError
        ? 'error'
        : deck === null || deck.cards.length === 0
          ? 'empty'
          : 'ready';

  return {
    status,
    deck: deck ?? EMPTY_DECK,
    setDeck,
    mutedReasons,
    now,
    retry: () => {
      void roster.refetch();
      void queue.refetch();
    },
  };
}
